import { nanoid } from '@reduxjs/toolkit';
import type { QuotePiece } from '../../../apis/fedexQuote';
import type { SelectedProduct } from '../components/ProductMultiSelectField';
import { ALL_PRODUCTS } from './boxProducts';

export const MAX_BOXES = 40;
export const BOX_REFERENCE_MAX_LENGTH = 100;
/** Dimensional-weight divisor for inches and pounds (FedEx and UPS daily rates). */
export const DIM_DIVISOR_IN_LB = 139;

/** Per-box values inside a line. */
export interface LineBox {
  reference?: string;
  /** Product id -> serial numbers typed for this box (comma separated). */
  serials?: Record<string, string>;
}

/** One package line: `qty` identical boxes. Lives in the form under `lines[]`. */
export interface PackageLineValues {
  /** Stable id for React keys and per-line product state; never sent to the API. */
  key: string;
  qty: number;
  /** Pounds, per box. */
  weight?: number | string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  /** Per-box data, index = box within the line; entries past `qty` are ignored. */
  boxes?: LineBox[];
}

/**
 * `validateFields()` resolves with registered fields only (no `key`/`qty`): read the lines with
 * `form.getFieldValue('lines')`. Entries are objects; a missing, repeated or non-string `key` is repaired by PackageLinesFields.
 */
export interface LinesFormValues {
  lines?: PackageLineValues[];
}

/** One expanded box, in carrier piece order. */
export interface BoxValues {
  weight?: number | string;
  reference?: string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
}

export interface PackageTotalsValues {
  boxes: number;
  actualLb: number;
  /** Undefined while any box has no dimensions. */
  billableLb?: number;
}

/** Random, not a counter: a counter restarts with each page load and could repeat a key the parent restores. */
export const newLineKey = (): string => `line-${nanoid(10)}`;

export const emptyLine = (): PackageLineValues => ({
  key: newLineKey(),
  qty: 1,
});

export const filled = (v: unknown): boolean =>
  v != null && String(v).trim() !== '';

/** Trimmed text of a form value that may arrive as a number or nothing. */
const text = (v: unknown): string => (v == null ? '' : String(v).trim());

export const positiveFiniteNumber = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

export const clampQty = (raw: unknown, max = MAX_BOXES): number => {
  const n = Math.floor(Number(raw));
  if (Number.isNaN(n) || n < 1) return 1;
  return Math.min(n, Math.max(1, max));
};

export const lineQty = (line?: Partial<PackageLineValues>): number =>
  clampQty(line?.qty);

/** Position of the line with this key, -1 when it is gone (positions shift when a line above is removed). */
export const indexOfKey = (lines: PackageLineValues[], key: string): number =>
  lines.findIndex((l) => l?.key === key);

export const totalBoxes = (lines: PackageLineValues[] = []): number =>
  lines.reduce((sum, l) => sum + lineQty(l), 0);

export const remainingCapacity = (lines: PackageLineValues[] = []): number =>
  Math.max(0, MAX_BOXES - totalBoxes(lines));

/** Index of the line's first box in carrier piece order. */
export const pieceOffset = (
  lines: PackageLineValues[],
  lineIndex: number
): number => totalBoxes(lines.slice(0, lineIndex));

/** Dimensions as carriers take them, whole inches of at least 1 each; undefined when any is missing or too small. */
const roundedDims = (
  b: Pick<BoxValues, 'length' | 'width' | 'height'>
): Required<Pick<QuotePiece, 'length' | 'width' | 'height'>> | undefined => {
  const [length, width, height] = [b.length, b.width, b.height].map((v) => {
    const n = positiveFiniteNumber(v) ?? 0;
    return n >= 1 ? Math.round(n) : 0;
  });
  return length >= 1 && width >= 1 && height >= 1
    ? { length, width, height }
    : undefined;
};

export const lineHasDims = (line: Partial<PackageLineValues>): boolean =>
  roundedDims(line) !== undefined;

/** The line's live boxes: entries past `qty` are ignored. */
export const boxesOf = (line: PackageLineValues): LineBox[] =>
  (line.boxes ?? []).slice(0, lineQty(line));

export const boxHasData = (box?: LineBox): boolean =>
  !!box &&
  (filled(box.reference) || Object.values(box.serials ?? {}).some(filled));

export const referenceCount = (line: PackageLineValues): number =>
  boxesOf(line).filter((b) => filled(b?.reference)).length;

/** Boxes at index >= `count` (within qty) holding a reference or serial: what lowering qty to `count` drops. */
export const boxesWithDataBeyond = (
  line: PackageLineValues,
  count: number
): number =>
  (line.boxes ?? []).slice(Math.max(count, 0), lineQty(line)).filter(boxHasData)
    .length;

/** True when a line holds anything the user typed (checked before a destructive remove). */
export const lineHasData = (
  line: PackageLineValues,
  products: SelectedProduct[] = []
): boolean =>
  [line.weight, line.length, line.width, line.height].some(filled) ||
  products.some((p) => p.id !== ALL_PRODUCTS) ||
  boxesOf(line).some(boxHasData);

/** Copy for "Duplicate": same sizes under a new key, no references or serials; qty is the original's capped at `capacity` but never below 1 (callers skip the copy when nothing fits). */
export const duplicateLine = (
  line: PackageLineValues,
  capacity: number
): PackageLineValues => ({
  key: newLineKey(),
  qty: clampQty(Math.min(lineQty(line), Math.max(1, capacity))),
  weight: line.weight,
  length: line.length,
  width: line.width,
  height: line.height,
});

const fmt = (v: unknown): string => String(positiveFiniteNumber(v) ?? v);

/** "N boxes · L × W × H in", with the whole inches the quote sends. */
export const lineTitle = (line: Partial<PackageLineValues>): string => {
  const qty = lineQty(line);
  const boxes = qty === 1 ? '1 box' : `${qty} boxes`;
  const dims = roundedDims(line);
  return dims
    ? `${boxes} · ${dims.length} × ${dims.width} × ${dims.height} in`
    : boxes;
};

/** Collapsed-line summary: weight, missing dims, products and references. */
export const lineSummary = (
  line: PackageLineValues,
  products: SelectedProduct[] = [],
  getProductName: (id: string) => string = (id) => id
): string => {
  const qty = lineQty(line);
  const multi = qty > 1;
  const parts = [
    positiveFiniteNumber(line.weight) !== undefined
      ? `${fmt(line.weight)} lb${multi ? ' each' : ''}`
      : 'No weight',
  ];
  if (!lineHasDims(line)) parts.push('No dimensions');
  const real = products.filter((p) => p.id !== ALL_PRODUCTS);
  if (real.length) {
    const items = real.reduce(
      (sum, p) => sum + Math.max(1, Number(p.quantity) || 1),
      0
    );
    parts.push(
      real.length === 1
        ? `${items} × ${getProductName(real[0].id)}${multi ? ' per box' : ''}`
        : `${real.length} products, ${items} items${multi ? ' per box' : ''}`
    );
  }
  const refs = boxesOf(line)
    .map((b) => text(b?.reference))
    .filter(Boolean);
  if (refs.length) {
    parts.push(multi ? `${refs.length} of ${qty} refs` : `Ref ${refs[0]}`);
  }
  return parts.join(' · ');
};

/** Every box in carrier piece order: line sizes repeated `qty` times, references per box. */
export const boxesFromLines = (lines: PackageLineValues[] = []): BoxValues[] =>
  lines.flatMap((l) =>
    Array.from({ length: lineQty(l) }, (_, i) => ({
      weight: l.weight,
      length: l.length,
      width: l.width,
      height: l.height,
      reference: l.boxes?.[i]?.reference,
    }))
  );

export const totalWeightLb = (boxes: BoxValues[]): number =>
  Math.round(
    boxes.reduce((sum, b) => sum + (positiveFiniteNumber(b.weight) ?? 0), 0) *
      100
  ) / 100;

export const minBoxWeightLb = (values: LinesFormValues): number | undefined => {
  const weights = (values.lines ?? [])
    .map((l) => positiveFiniteNumber(l.weight))
    .filter((w): w is number => w !== undefined);
  return weights.length ? Math.min(...weights) : undefined;
};

/** Every line's weight path, for antd `dependencies` on the lightest-box rule: re-validates as weights are typed; lines added or removed are checked at submit. */
export const LINE_WEIGHT_DEPENDENCIES: Array<Array<string | number>> =
  Array.from({ length: MAX_BOXES }, (_, i) => ['lines', i, 'weight']);

/** Sum of per-box billable weights, each the larger of actual and L×W×H/139 on the whole-inch dims the carrier sees (rounded up). */
export const billableWeightLb = (boxes: BoxValues[]): number | undefined => {
  let total = 0;
  for (const b of boxes) {
    const dims = roundedDims(b);
    if (!dims) return undefined;
    const dimLb = Math.ceil(
      (dims.length * dims.width * dims.height) / DIM_DIVISOR_IN_LB
    );
    total += Math.max(Math.ceil(positiveFiniteNumber(b.weight) ?? 0), dimLb);
  }
  return total;
};

export const packageTotals = (
  lines: PackageLineValues[] = []
): PackageTotalsValues => {
  const boxes = boxesFromLines(lines);
  return {
    boxes: boxes.length,
    actualLb: totalWeightLb(boxes),
    billableLb: billableWeightLb(boxes),
  };
};

/** Whole-inch length/width/height/unit when all three dimensions round to at least 1, else nothing. */
export const dimsPayload = (b: BoxValues): Partial<QuotePiece> => {
  const dims = roundedDims(b);
  return dims ? { ...dims, dimensionUnit: 'IN' } : {};
};

/** `pieces[]` for more than one box; undefined keeps the flat single-package fields. */
export const buildPieces = (
  values: LinesFormValues
): QuotePiece[] | undefined => {
  const boxes = boxesFromLines(values.lines);
  if (boxes.length < 2) return undefined;
  return boxes.map((b) => {
    const reference = text(b.reference);
    return {
      weight: positiveFiniteNumber(b.weight) ?? 0,
      weightUnit: 'LB',
      ...(reference ? { reference } : {}),
      ...dimsPayload(b),
    };
  });
};

/** The only box, for the flat single-package payload. */
export const singleBox = (values: LinesFormValues): BoxValues =>
  boxesFromLines(values.lines)[0] ?? {};

/** A line's selection from the owner's map. Array check, not `??`: a stored key could name an Object.prototype member. */
export const productsForKey = (
  productsByKey: Record<string, SelectedProduct[]>,
  key: string
): SelectedProduct[] => {
  const own = productsByKey[key];
  return Array.isArray(own) ? own : [];
};

/** Product list per box in piece order: quantities from the line, serials from the box; `all_products` is dropped. */
export const expandLineProducts = (
  lines: PackageLineValues[] = [],
  productsByKey: Record<string, SelectedProduct[]> = {}
): SelectedProduct[][] =>
  lines.flatMap((l) => {
    const products = productsForKey(productsByKey, l.key).filter(
      (p) => p.id !== ALL_PRODUCTS
    );
    return Array.from({ length: lineQty(l) }, (_, i) => {
      const serials = l.boxes?.[i]?.serials ?? {};
      return products.map(({ id, quantity }) => {
        const serial = Object.prototype.hasOwnProperty.call(serials, id)
          ? text(serials[id])
          : '';
        return { id, quantity, ...(serial ? { serial_number: serial } : {}) };
      });
    });
  });
